import type { ConfigurableProperty } from 'api/digitalTwin/digitalTwin.api'
import { allowsPartialTagMatching } from 'helpers/filterComponent.helper/filterComponent.helper'
import {
  getFilteredProperties,
  getFilteredPropertiesByPriority,
} from 'utils/digitalTwinSettings/digitalTwinSettingUtils'

function toPriorities(priorityLevels: number[]): SystemSettingPriorityLevel[] {
  return priorityLevels.map((priorityLevel) => ({ priority_level: priorityLevel, display_name: '' }))
}

/**
 * The properties the header filter selects for an Optimization type option: every property available at
 * one of the option's priority levels, whichever priority tags the header has toggled.
 */
export function getSelectedProperties(
  allProperties: ConfigurableProperty[],
  priorityLevels: number[],
  filterTags: string[]
): ConfigurableProperty[] {
  return getFilteredProperties(allProperties, toPriorities(priorityLevels), filterTags)[0]
}

export function getPropertiesAtPriorityLevels(
  allProperties: ConfigurableProperty[],
  priorityLevels: number[]
): ConfigurableProperty[] {
  return getFilteredPropertiesByPriority(allProperties, toPriorities(priorityLevels))[0]
}

function getTagCategory(tag: string): string {
  return tag.split('/')[0]
}

/**
 * The properties a filter selects when it offers only some tag categories: those at one of the priority
 * levels that match the filter tags in every offered category they have a tag in. A category the filter
 * does not offer, such as `property_attribute` on a header filter built from units, decides nothing.
 */
export function getSelectedPropertiesByOfferedTags(
  allProperties: ConfigurableProperty[],
  priorityLevels: number[],
  filterTags: string[],
  offeredTags: string[][]
): ConfigurableProperty[] {
  const offeredCategories = new Set(offeredTags.flat().map(getTagCategory))

  return getPropertiesAtPriorityLevels(allProperties, priorityLevels).filter((property) => {
    const offeredPropertyTags = (property.tags ?? []).filter((tag) => offeredCategories.has(getTagCategory(tag)))

    return offeredPropertyTags.length > 0 && allowsPartialTagMatching(offeredPropertyTags, filterTags)
  })
}
